import { useState } from "react";
import styled from "styled-components";
import { Box } from "../Box";
import { Flex } from "../Flex";
import { Link } from "../Link";
import * as questionIcons from "../../assets/questions";
import { axios } from "../../data/axios";
import { useAuth } from "../../context/AuthContext";
import { useCountries } from "../../data/useCountries";
import { showError } from "../../utils/error-handling";

/**
 * Shared display kit for captured form answers.
 *
 * Two areas render the same thing and previously did it two different ways:
 * the Profile Forms tab (answers from every Individual record attached to the
 * Profile, so a question can hold several answers over time) and the
 * Applications Individual View Forms tab (the single set of answers captured
 * for that one application). Both now render through `AnswerTable`.
 *
 * The table is `table-layout: fixed`, which is the point: question and answer
 * columns sit on the same axis in every section of every form regardless of
 * how long a question title runs. The previous percentage-width flex rows
 * shrank by different amounts per row, so the answer column drifted.
 */

/* ------------------------------------------------------------------ groups */

/** Airy white card that groups one section's questions. */
export const SectionCard = styled(Box)`
  ${({ theme: { colors, radii, shadows, space } }) => `
    background: ${colors.white};
    border: 1px solid ${colors.gray[100]};
    border-radius: ${radii[1]};
    box-shadow: ${shadows.sm};
    padding: ${space[1]} ${space[3]} ${space[3]};
    margin-bottom: ${space[2]};
  `}
`;

/** Uppercase section label with a hairline underline. */
export const SectionTitle = styled.div`
  ${({ theme: { colors, space } }) => `
    padding: ${space[2]} 0 ${space[1]};
    margin-bottom: ${space[1]};
    border-bottom: 1px solid ${colors.gray[100]};
    font-family: "Inter", sans-serif;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${colors.gray[60]};
  `}
`;

/** Collapsible form-level title (passed into ExpansionPanel). */
export const FormTitle = styled.span`
  ${({ theme: { colors } }) => `
    font-family: "Inter", sans-serif;
    font-size: 15px;
    font-weight: 700;
    color: ${colors.gray[80]};
  `}
`;

/* ------------------------------------------------------------------- table */

/**
 * Rows are separated by alternating bands rather than a hairline each, which
 * keeps a ten-question section from turning into a stack of rules. The band is
 * pulled 12px past the text either side (negative margin cancelling the cell
 * padding) so question text still lines up with the section title above it.
 * `border-collapse: separate` is required for the rounded band corners.
 */
const BAND_BLEED = "12px";

const Table = styled.table`
  ${({ theme: { colors, radii } }) => `
    width: calc(100% + ${BAND_BLEED} * 2);
    margin: 0 -${BAND_BLEED};
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 0;

    tr:nth-of-type(even) {
      background: ${colors.gray[5]};
    }

    tr:nth-of-type(even) td:first-child {
      border-top-left-radius: ${radii[0]};
      border-bottom-left-radius: ${radii[0]};
    }

    tr:nth-of-type(even) td:last-child {
      border-top-right-radius: ${radii[0]};
      border-bottom-right-radius: ${radii[0]};
    }
  `}
`;

const Cell = styled.td`
  ${({ theme: { space } }) => `
    vertical-align: top;
    padding: 10px ${BAND_BLEED};

    &:first-child {
      width: 38%;
      padding-right: ${space[3]};
    }
  `}
`;

const QuestionInner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
`;

const QuestionIcon = styled.img`
  width: 18px;
  height: 18px;
  flex: none;
  margin-top: 2px;
  opacity: 0.7;
`;

const QuestionTitle = styled.div`
  ${({ theme: { colors, fontWeights } }) => `
    font-family: "Inter", sans-serif;
    font-size: 13.5px;
    font-weight: ${fontWeights.medium};
    line-height: 1.4;
    color: ${colors.gray[80]};
    word-break: break-word;
  `}
`;

const QuestionType = styled.div`
  ${({ theme: { colors } }) => `
    font-family: "Inter", sans-serif;
    font-size: 11px;
    color: ${colors.gray[50]};
    margin-top: 2px;
  `}
`;

/**
 * Two-column question/answer table. Wrap `AnswerRow` children in it. There is
 * no column header: the banded rows make the two columns self-evident, and a
 * "Question / Answer" header repeated above every section card added a rule and
 * three repeats per form for no information.
 */
export const AnswerTable = ({ children }: { children: React.ReactNode }) => (
  <Table>
    <tbody>{children}</tbody>
  </Table>
);

interface AnswerRowFieldType {
  name?: string;
  slug_name?: string;
}

/**
 * One question row. The answer cell takes whatever the caller renders, so the
 * Profile "By question" view can hang a change tag and an answer history off
 * the same row shape the Applications view uses for a single value.
 */
export const AnswerRow = ({
  title,
  fieldType,
  children,
}: {
  title?: string | null;
  fieldType?: AnswerRowFieldType | null;
  children: React.ReactNode;
}) => {
  const slug = fieldType?.slug_name;
  // Unknown slugs are possible (the icon set is a fixed list); render without
  // an icon rather than an <img> with an undefined src.
  const icon = slug ? (questionIcons as Record<string, string>)[slug] : undefined;

  return (
    <tr>
      <Cell>
        <QuestionInner>
          {icon && <QuestionIcon src={icon} alt="" />}
          <Box minWidth={0}>
            <QuestionTitle>{title || "Question"}</QuestionTitle>
            {fieldType?.name && <QuestionType>{fieldType.name}</QuestionType>}
          </Box>
        </QuestionInner>
      </Cell>
      <Cell>{children}</Cell>
    </tr>
  );
};

/* ------------------------------------------------------------------ values */

const filenameFromUrl = (url: string) => {
  try {
    return new URL(url).pathname.split("/").pop() || "document";
  } catch {
    return "document";
  }
};

/** A plain `<a href>` to this URL 401s — it needs the auth header attached. */
export const DocumentLink = ({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) => {
  const { authHeader } = useAuth();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      const response = await axios.get(href, {
        headers: { Authorization: authHeader },
        responseType: "arraybuffer",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(new Blob([response.data]));
      link.download = filenameFromUrl(href);
      link.click();
    } catch (err) {
      showError(err, "A problem occurred while opening the document.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Link href={href} onClick={handleClick} fontSize={1}>
      {children}
    </Link>
  );
};

const isSafeHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

/** Renders "link" answers as a new-tab link and "country" answers with a flag. */
export const AnswerValue = ({
  value,
  fieldType,
}: {
  value: string;
  fieldType?: AnswerRowFieldType | null;
}) => {
  const { data: countriesData } = useCountries();

  if (fieldType?.slug_name === "link" && isSafeHttpUrl(value)) {
    return (
      <Link href={value} target="_blank" rel="noopener noreferrer" fontSize={1}>
        {value}
      </Link>
    );
  }

  if (fieldType?.slug_name === "country") {
    const countryIcon = countriesData?.data.find(
      (country) => country.name.toLowerCase() === value.toLowerCase(),
    )?.icon;

    if (countryIcon) {
      return (
        <Flex alignItems="center" gap="8">
          <Box backgroundColor="gray.10" borderRadius="50%" p="1">
            <img
              src={`data:image/png;base64,${countryIcon}`}
              alt=""
              style={{
                display: "block",
                width: 16,
                height: 16,
                borderRadius: "50%",
              }}
            />
          </Box>
          {value}
        </Flex>
      );
    }
  }

  return <>{value}</>;
};

export const FieldValue = styled.div<{ muted?: boolean }>`
  ${({ theme: { colors }, muted }) => `
    font-family: "Inter", sans-serif;
    font-size: 14px;
    line-height: 1.45;
    color: ${muted ? colors.gray[40] : colors.gray[80]};
    word-break: break-word;
  `}
`;

/** Small amber pill flagging an answer that changed from the previous one. */
export const ChangedTag = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #9a5b00;
  background: #fbede0;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
`;

/** Muted metadata line, e.g. "Latest · BIKE-629434 · 17/06/2026". */
export const MetaLine = styled.div`
  ${({ theme: { colors } }) => `
    font-family: "Inter", sans-serif;
    font-size: 12px;
    color: ${colors.gray[50]};
    margin-bottom: 2px;
  `}
`;

/** Quiet disclosure button for "N earlier answers". */
export const DisclosureButton = styled.button`
  ${({ theme: { colors, space } }) => `
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: "Inter", sans-serif;
    font-size: 12.5px;
    font-weight: 600;
    color: ${colors.gray[60]};
    background: transparent;
    border: 0;
    padding: ${space[1]} 0 0;
    cursor: pointer;
    transition: color 0.12s ease;

    &:hover { color: ${colors.blue.primary}; }
    &:focus-visible {
      outline: 2px solid ${colors.blue.primary};
      outline-offset: 2px;
      border-radius: 2px;
    }
  `}
`;

/** One prior-answer row revealed under the disclosure. */
export const HistoryRow = styled(Box)`
  ${({ theme: { colors, space } }) => `
    padding: ${space[0]} 0 ${space[0]} ${space[2]};
    margin-top: ${space[1]};
    border-left: 2px solid ${colors.gray[100]};
  `}
`;
